// =============================================================================
// LP Express — "PapaBear" landing-page label content model  (task 2562).
//
// This module owns the ENTIRE editable copy for the PapaBear dry-skin
// long-form landing page.  Every field maps 1-to-1 to a piece of on-page text
// identified in the PapaBear Master Landing Page Export inventory.
//
// Design contract
// ───────────────
// • Prices, Shopify variant IDs, discount percentages, and customer-count
//   claims are NOT stored here — they belong in live product/pricing data or
//   runtime state.  The buy-box section stores only editable *labels* (e.g.
//   "Subscribe, 30 days", "One-time") and supported-option metadata.
// • Hero and review social-proof copy intentionally omit the hardcoded "4.9"
//   rating and "64,000+" customer count; truthful values must be injected at
//   render-time from real data.
// • CTA defaults are honest and discount-neutral ("Shop the bundle" /
//   "Add to cart"). Live price and discount treatment belongs to the shared
//   LP receipt renderer so a later sale change cannot leave copied claims stale.
// • No server/client-specific imports.  Pure TypeScript, safe in both
//   environments.
// =============================================================================

// ── Section catalog (in render order) ────────────────────────────────────────

export type LpPapabearLabelSectionKey =
  | "hero"
  | "reasons"
  | "buyBox"
  | "reviews"
  | "faq"
  | "stickyBar";

export const LP_PAPABEAR_LABEL_SECTIONS: {
  key: LpPapabearLabelSectionKey;
  label: string;
}[] = [
  { key: "hero", label: "Hero" },
  { key: "reasons", label: "Five Reasons" },
  { key: "buyBox", label: "Bundle Buy Box" },
  { key: "reviews", label: "Customer Reviews" },
  { key: "faq", label: "FAQ" },
  { key: "stickyBar", label: "Sticky Purchase Bar" },
];

export const LP_PAPABEAR_LABEL_SECTION_KEYS = LP_PAPABEAR_LABEL_SECTIONS.map(
  (s) => s.key,
);

export function lpPapabearLabelSectionLabel(key: string): string | undefined {
  return LP_PAPABEAR_LABEL_SECTIONS.find((s) => s.key === key)?.label;
}

// ── Media slot helpers ────────────────────────────────────────────────────────
// Each slot key maps to a single image surface.  Renderers use these as
// generatedContent.mediaOverrides keys so the standard onChooseMedia /
// onUploadMedia mechanism can swap them out without touching this file.

export const LP_PAPABEAR_MEDIA_SLOTS = [
  "hero-image",
  "reason-01-image",
  "reason-02-image",
  "reason-03-image",
  "reason-04-image",
  "reason-05-image",
  "gallery-01",
  "gallery-02",
  "gallery-03",
  "gallery-04",
  "gallery-05",
  "gallery-06",
] as const;

export type LpPapabearMediaSlot = (typeof LP_PAPABEAR_MEDIA_SLOTS)[number];

export function isLpPapabearMediaSlot(key: string): key is LpPapabearMediaSlot {
  return (LP_PAPABEAR_MEDIA_SLOTS as readonly string[]).includes(key);
}

// ── Content types ─────────────────────────────────────────────────────────────

export interface LpPapabearTrustItem {
  label: string;
}

export interface LpPapabearHeroContent {
  /** Small all-caps kicker above the headline, e.g. "FOR DRY SKIN" */
  eyebrow: string;
  /** Main hero headline */
  headline: string;
  /** Hero body paragraph */
  body: string;
  /** Primary CTA button label — honest, discount-neutral by default */
  cta: string;
  /** Short reassurance items beneath the CTA */
  trustItems: LpPapabearTrustItem[];
}

export interface LpPapabearReasonItem {
  /** Zero-padded display number, e.g. "01" */
  number: string;
  /** Small editorial evidence label, e.g. "FOUND ON LABEL" */
  label: string;
  /** Coral italic observation that sets up the reason */
  observation: string;
  /** Blue conclusion heading beneath the editorial arrow */
  heading: string;
  /** Supporting body paragraph */
  body: string;
}

export interface LpPapabearReasonsContent {
  items: LpPapabearReasonItem[];
}

export interface LpPapabearPurchaseOption {
  /** Radio-label text, e.g. "Subscribe, 30 days" or "One-time" */
  label: string;
  /** Whether this option is a subscription (used for disclosure visibility) */
  isSubscription: boolean;
  /** Disclosure shown when this option is selected, only for subscriptions */
  disclosure: string;
}

export interface LpPapabearBuyBoxContent {
  /** Small all-caps kicker, e.g. "GET STARTED" */
  eyebrow: string;
  /**
   * Product heading — editable fallback used only when the page has no selected
   * LP product. A selected product name is always rendered live instead.
   */
  productHeading: string;
  /** Subheadline beneath the product heading */
  subhead: string;
  /** Labels for the included feature bullets */
  includedFeatures: string[];
  /** Label above the purchase-mode radio group */
  purchaseFieldLabel: string;
  /** Supported purchase options (labels only — no prices or IDs here) */
  purchaseOptions: LpPapabearPurchaseOption[];
  /** Label above the scent/variant selector */
  scentFieldLabel: string;
  /** Scent option display names (order preserved; IDs live in runtime config) */
  scentOptions: string[];
  /** "Add to cart" button label */
  addToCartLabel: string;
  /** Shipping reassurance line */
  shippingNote: string;
  /** Accessible label for previous gallery button */
  galleryPrevLabel: string;
  /** Accessible label for next gallery button */
  galleryNextLabel: string;
}

export interface LpPapabearReviewCard {
  /** Review title / pull-quote headline */
  title: string;
  /** Full review body */
  quote: string;
  /** Attribution / reviewer name */
  attribution: string;
}

export interface LpPapabearReviewsContent {
  /** Small all-caps kicker above the heading, e.g. "REAL CUSTOMERS" */
  eyebrow: string;
  /** Section heading */
  heading: string;
  /**
   * Rating summary label — intentionally omits hardcoded 64 000 / 4.9 values.
   * Renderer should inject live data; this is the editable fallback copy.
   */
  ratingSummaryLabel: string;
  /** Three source review cards */
  items: LpPapabearReviewCard[];
}

export interface LpPapabearFaqItem {
  q: string;
  a: string;
}

export interface LpPapabearFaqContent {
  /** Small all-caps kicker */
  eyebrow: string;
  /** Section heading */
  heading: string;
  items: LpPapabearFaqItem[];
}

export interface LpPapabearStickyBarContent {
  /**
   * Offer label shown in the sticky bar — honest/discount-neutral by default.
   */
  offer: string;
  /** CTA button label in the sticky bar */
  cta: string;
}

/** Root content type for the PapaBear landing page. */
export interface LpPapabearLabelContent {
  hero: LpPapabearHeroContent;
  reasons: LpPapabearReasonsContent;
  buyBox: LpPapabearBuyBoxContent;
  reviews: LpPapabearReviewsContent;
  faq: LpPapabearFaqContent;
  stickyBar: LpPapabearStickyBarContent;
}

export interface LpPapabearLabelImageSlotDefinition {
  key: `reason-0${1 | 2 | 3 | 4 | 5}-image`;
  variantHint: string;
  aspectRatio: "4:3";
}

// ── Default content (seeded verbatim from the export inventory) ───────────────
// Numeric claims (64 000 customers, 4.9 rating, 4.95 rating, 32% discount) are
// deliberately omitted or neutralised here.  CTA labels are honest defaults
// without discount wording.

export const LP_PAPABEAR_LABEL_DEFAULT_CONTENT: LpPapabearLabelContent = {
  // ── Hero ──────────────────────────────────────────────────────────────────
  hero: {
    eyebrow: "FOR DRY SKIN",
    headline:
      "5 reasons people with dry skin are switching to tallow",
    body:
      "If you are reapplying lotion three times a day and still feel tight by lunch, the problem might not be how much you are using. It might be what is in the bottle.",
    // Honest default — no discount claim until confirmed by live facts.
    cta: "Shop the bundle",
    trustItems: [
      { label: "Small-batch" },
      { label: "Made in the USA" },
    ],
  },

  // ── Five Reasons ─────────────────────────────────────────────────────────
  reasons: {
    items: [
      {
        number: "01",
        label: "FOUND ON LABEL",
        observation: "Water is listed first. That means it's mostly water.",
        heading: "Tallow's first ingredient is tallow.",
        body:
          "The first ingredient on almost every drugstore moisturizer is aqua, which is water. Cosmetic labels list ingredients by weight, so water is what you're mostly buying. Tallow has none. Every ounce is concentrated fat, oils, and wax doing actual work.",
      },
      {
        number: "02",
        label: "FOUND ON LABEL",
        observation:
          "Alcohol and synthetic fragrance can be hard on sensitive skin.",
        heading: "No alcohol, no synthetic fragrance",
        body:
          "Denatured alcohol and synthetic fragrance are two of the most common irritants in mass-market skincare, and neither one goes in a Papabear jar. If a moisturizer has ever stung on application, those are worth looking for on the label.",
      },
      {
        number: "03",
        label: "FOUND ON LABEL",
        observation: "Tallow. Olive oil. Beeswax. Honey.",
        heading: "Ingredients you can actually pronounce",
        body:
          "Tallow. Olive oil. Beeswax. Honey. That is most of the label. No fillers to bulk out the jar, and no preservative system built to keep it stable on a shelf for eighteen months.",
      },
      {
        number: "04",
        label: "ONE JAR, HEAD TO TOE",
        observation: "Dry skin does not stop at your jawline.",
        heading: "One jar from face to heels",
        body:
          "Dry skin does not stop at your jawline. The same balm goes on hands, elbows, knuckles and heels - which is usually where it gets used the most once people realize they can.",
      },
      {
        number: "05",
        label: "HOW IT'S MADE",
        observation: "The process matters as much as the ingredients.",
        heading: "Grass-fed tallow, rendered in small batches",
        body:
          "Rendered low and slow until it turns clean and white, then whipped in small runs rather than mixed from a pre-made base. This ensures every Papabear product is of the highest quality.",
      },
    ],
  },

  // ── Bundle Buy Box ────────────────────────────────────────────────────────
  buyBox: {
    eyebrow: "GET STARTED",
    // Honest fallback heading; overridden at runtime from product data.
    productHeading: "The Farm to Skin Essentials Bundle",
    subhead:
      "The complete face routine. Pick your lip balm scent and add it straight to your cart.",
    includedFeatures: [
      "Complete 4-piece face routine",
      "Restorative Tallow Balm",
      "Revival Eye Cream",
      "Zero synthetics, ever",
    ],
    purchaseFieldLabel: "Choose your purchase",
    purchaseOptions: [
      {
        label: "Subscribe, 30 days",
        isSubscription: true,
        disclosure: "Renews every 30 days. Cancel anytime.",
      },
      {
        label: "One-time",
        isSubscription: false,
        disclosure: "",
      },
    ],
    scentFieldLabel: "Choose your lip balm scent",
    // Scent display names only — Shopify variant IDs live in runtime config.
    scentOptions: [
      "Pumpkin Spice",
      "Vanilla",
      "Lavender",
      "Peppermint",
      "Orange Cream",
      "Raw Honey",
    ],
    // Honest default — no discount claim until confirmed by live facts.
    addToCartLabel: "Add to cart",
    shippingNote: "Shipping options are shown at checkout",
    galleryPrevLabel: "Previous images",
    galleryNextLabel: "Next images",
  },

  // ── Customer Reviews ──────────────────────────────────────────────────────
  reviews: {
    eyebrow: "REAL CUSTOMERS",
    heading: "What people with dry skin say",
    // Neutral label — renderer injects live rating/count data at runtime.
    ratingSummaryLabel: "Verified customer reviews",
    items: [
      {
        title: "\"These Are Saving Me This Winter!\"",
        quote:
          "These lip balms are a lifesaver for dry lips. Love the clean, natural ingredients and the Orange Cream scent! Made in the USA, so I feel great about my purchase.",
        attribution: "Sarah T.",
      },
      {
        title: "\"Worth Every Penny!\"",
        quote:
          "At nearly 55, I thought it was too late to care for my skin. But after just a couple of weeks with this tallow, my skin feels more elastic and deeply moisturized. It has never looked better!",
        attribution: "Miranda G.",
      },
      {
        title: "\"Great for Gifting\"",
        quote:
          "The Raw Honey balm is a must-have for winter! My lips were always chapped before trying these, but now they feel soft and moisturized all day. I gave a couple to my roommates, and they are hooked too!",
        attribution: "Jessica H.",
      },
    ],
  },

  // ── FAQ ──────────────────────────────────────────────────────────────────
  faq: {
    eyebrow: "FREQUENTLY ASKED QUESTIONS",
    heading: "Got a question?",
    items: [
      {
        q: "Does it feel waxy or greasy?",
        a:
          "Not at all. It goes on smooth and absorbs quickly - no sticky film and no greasy residue.",
      },
      {
        q: "Is it safe for sensitive skin?",
        a:
          "Absolutely. Tallow is one of the most skin friendly fats and we keep our formulas minimal.",
      },
      {
        q: "Where is it made?",
        a:
          "Right here in the USA. Every batch is handmade in small quantities to ensure quality, consistency, and care.",
      },
      {
        q: "How much should I use?",
        a:
          "Less than you think. Because there is no water in the jar, a pea-sized amount covers what a palmful of lotion would. Start small and add more only if you need it.",
      },
    ],
  },

  // ── Sticky Purchase Bar ───────────────────────────────────────────────────
  stickyBar: {
    // Honest default — no discount claim until confirmed by live facts.
    offer: "Bundle details & checkout",
    cta: "Add to Cart",
  },
};

// PapaBear's commerce gallery intentionally stays on real LP product media.
// These are the five editorial story surfaces that follow Long Form's
// Sim-prompt → Fal-render → R2-persist pipeline. The selected product photo is
// supplied to the model as a fidelity reference, but each brief asks for a
// distinct editorial scene instead of another isolated packshot.
export function papabearLabelImageSlotsFor(
  content: unknown,
): LpPapabearLabelImageSlotDefinition[] {
  const normalized = normalizeLpPapabearLabelContent(content);
  const sceneDirections = [
    "A warm bathroom-counter editorial still life contrasting a watery lotion texture with a rich balm texture. The selected product may appear naturally at the edge of the scene, but it must not be an isolated packshot.",
    "A calm, fragrance-free skincare ritual on sensitive dry skin in a warm, uncluttered home. Show gentle application and comfort; avoid perfume bottles, synthetic laboratory imagery, redness, or medical claims.",
    "An overhead ingredient still life featuring visually recognizable olive oil, beeswax, honey, and rich balm texture. Keep the selected product as a secondary, label-faithful anchor rather than the entire composition.",
    "A warm lifestyle photograph showing one simple balm used across hands, elbows, and heels. Make the human routine the subject; the selected product is a supporting element, not a centered catalog photo.",
    "A small-batch craft scene with a stainless mixing bowl, creamy whipped balm texture, and careful hand finishing in a warm workshop. Suggest patient craft without inventing certifications, facilities, or claims.",
  ] as const;
  return normalized.reasons.items.slice(0, 5).map((reason, index) => ({
    key: `reason-0${index + 1}-image` as LpPapabearLabelImageSlotDefinition["key"],
    aspectRatio: "4:3",
    variantHint: [
      "Create a warm, premium editorial lifestyle photograph for one numbered reason in a long-form skincare landing page.",
      `Reason observation: ${reason.observation}`,
      `Reason headline: ${reason.heading}`,
      `Reason context: ${reason.body}`,
      sceneDirections[index],
      "Use natural ochre, cream, honey, and warm-brown tones with soft daylight and tactile detail. Preserve any visible product packaging and label from the reference image accurately. Do not add readable text, prices, discounts, ratings, badges, customer counts, or unsupported claims.",
    ].join("\n"),
  }));
}

export const LP_PAPABEAR_LABEL_IMAGE_SLOTS =
  papabearLabelImageSlotsFor(LP_PAPABEAR_LABEL_DEFAULT_CONTENT);

export function lpPapabearLabelImagesNeedGeneration(value: unknown): boolean {
  if (!Array.isArray(value)) return true;
  return LP_PAPABEAR_LABEL_IMAGE_SLOTS.some((definition) => {
    const slot = value.find((item: any) => item?.key === definition.key);
    const readyWithDurableUrl =
      slot?.imageStatus === "ready" &&
      typeof slot?.imageUrl === "string" &&
      slot.imageUrl.trim().length > 0;
    return (
      !slot ||
      (!readyWithDurableUrl &&
        slot.imageStatus !== "failed" &&
        slot.imageStatus !== "none")
    );
  });
}

export type LpPapabearLabelImageRecoveryAction =
  | "generate-prompt"
  | "fail-ambiguous-prompt"
  | "submit-fal"
  | "fail-ambiguous-fal"
  | "resume-fal"
  | "terminal";

/**
 * Resolve restart behavior without making a provider call. Sim does not expose
 * a resumable request id, so a crash after its submission boundary fails safe
 * for an explicit user retry. Fal does expose one and is resumed when present.
 */
export function resolveLpPapabearLabelImageRecoveryAction(
  value: unknown,
): LpPapabearLabelImageRecoveryAction {
  const slot = value && typeof value === "object" ? (value as any) : {};
  if (slot.imageStatus !== "pending") return "terminal";
  const hasPrompt =
    typeof slot.imagePrompt === "string" &&
    slot.imagePrompt.trim().length > 0 &&
    typeof slot.referenceImageUrl === "string" &&
    slot.referenceImageUrl.trim().length > 0;
  if (!hasPrompt) {
    return slot.promptSubmissionStartedAt
      ? "fail-ambiguous-prompt"
      : "generate-prompt";
  }
  if (
    typeof slot.falRequestId === "string" &&
    slot.falRequestId.trim().length > 0
  ) {
    return "resume-fal";
  }
  return slot.falSubmissionStartedAt
    ? "fail-ambiguous-fal"
    : "submit-fal";
}

// ── Normalization ─────────────────────────────────────────────────────────────
// Shape-preserving deep overlay.  Raw values are accepted only when they match
// the default's type at the same structural position.  Anything missing or
// mistyped falls back to the default so the renderer can never crash on a
// partial or drifted payload.  Any new editable field added to
// LpPapabearLabelContent MUST also be added to LP_PAPABEAR_LABEL_DEFAULT_CONTENT
// or it will be silently dropped here (same rule as other label modules).

function overlay(def: unknown, raw: unknown): unknown {
  if (typeof def === "string") return typeof raw === "string" ? raw : def;
  if (typeof def === "number") {
    const n = typeof raw === "number" ? raw : Number(raw);
    return Number.isFinite(n) ? n : def;
  }
  if (typeof def === "boolean") return typeof raw === "boolean" ? raw : def;
  if (Array.isArray(def)) {
    if (!Array.isArray(raw)) return def;
    const template = def[0];
    if (template === undefined) return raw;
    const count = Math.min(40, Math.max(def.length, raw.length));
    return Array.from({ length: count }, (_, index) => {
      const itemDefault = def[index] ?? template;
      return overlay(itemDefault, raw[index]);
    });
  }
  if (def && typeof def === "object") {
    const src =
      raw && typeof raw === "object" && !Array.isArray(raw)
        ? (raw as Record<string, unknown>)
        : {};
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(def as object)) {
      out[k] = overlay(
        (def as Record<string, unknown>)[k],
        src[k],
      );
    }
    return out;
  }
  return raw !== undefined && raw !== null ? raw : def;
}

/** Deep-normalize an arbitrary value into a fully-valid LpPapabearLabelContent. */
export function normalizeLpPapabearLabelContent(
  raw: unknown,
): LpPapabearLabelContent {
  return overlay(
    LP_PAPABEAR_LABEL_DEFAULT_CONTENT,
    raw,
  ) as LpPapabearLabelContent;
}

// ── Usability guard ───────────────────────────────────────────────────────────

/**
 * Returns true when the blob carries enough structure to render the page
 * without hitting undefined reads.  A renderer should call this after
 * `normalizeLpPapabearLabelContent` and show a loading/error state when false.
 */
export function isLpPapabearLabelContentUsable(
  content: LpPapabearLabelContent | null | undefined,
): boolean {
  return Boolean(
    content &&
      content.hero?.headline &&
      content.reasons?.items?.length === 5 &&
      content.buyBox?.productHeading &&
      content.faq?.items?.length > 0,
  );
}

/** Keep bundle identity anchored to the selected parent even when LP Express
 * expands the render list into member SKUs for receipt/gallery rendering. */
export function resolvePapabearLabelRootProduct<T>(
  rootProduct: T | null | undefined,
  expandedProducts: readonly T[],
): T | undefined {
  return rootProduct ?? expandedProducts[0];
}

type PapabearProductMedia = {
  mediaJson?: unknown;
  adgenImageRefs?: unknown;
};

function papabearProductMediaUrls(product: unknown): string[] {
  if (!product || typeof product !== "object") return [];
  const candidate = product as PapabearProductMedia;
  const durableMedia = Array.isArray(candidate.mediaJson)
    ? candidate.mediaJson.filter(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0,
      )
    : [];
  if (durableMedia.length > 0) return durableMedia;
  return Array.isArray(candidate.adgenImageRefs)
    ? candidate.adgenImageRefs.filter(
        (value): value is string =>
          typeof value === "string" && value.trim().length > 0,
      )
    : [];
}

/**
 * Resolve real product imagery for every PapaBear image slot.
 *
 * LP Express expands bundles into member SKUs for receipt rendering, but the
 * selected parent usually owns the bundle's gallery. Keep that parent first,
 * then append member galleries so the hero never goes blank merely because the
 * first expanded member has no media.
 */
export function resolvePapabearLabelProductImages<T>(
  rootProduct: T | null | undefined,
  expandedProducts: readonly T[],
): string[] {
  const urls: string[] = [];
  const seen = new Set<string>();
  for (const product of [rootProduct, ...expandedProducts]) {
    for (const url of papabearProductMediaUrls(product)) {
      if (seen.has(url)) continue;
      seen.add(url);
      urls.push(url);
    }
  }
  return urls;
}

// ── Generated-copy claim guard ───────────────────────────────────────────────
// Reused LP Express workflows were designed for broader sales-page templates
// and may return price, discount, review-count, or shipping claims. PapaBear
// renders those facts from the live receipt/review data instead, so generated
// text carrying one of those claims falls back to the current saved value.

const PAPABEAR_UNSAFE_GENERATED_CLAIM_PATTERNS = [
  /[$€£]\s*\d/i,
  /\b\d+(?:[.,]\d+)?\s*%/i,
  /\b(?:dollars?|bucks?|cents?)\b/i,
  /\b(?:price|priced|costs?|only)\s+(?:at\s+)?(?:\d|one|two|three|four|five|six|seven|eight|nine|ten|twenty|thirty|forty|fifty|hundred)/i,
  /\b(?:save|saving|savings|discount|discounted|deal|sale|promo|promotion)\b/i,
  /\boff\b/i,
  /\b(?:half|third|quarter)\s+(?:the\s+)?price\b/i,
  /\bbuy\s+(?:one|1)\s+get\s+(?:one|1)\b/i,
  /\bbogo\b/i,
  /\b(?:two|2)[\s-]+for[\s-]+(?:one|1)\b/i,
  /\b(?:complimentary|no[\s-]?charge)\b/i,
  /\b(?:shipping|delivery)\b/i,
  /\b\d[\d,\s.]*\+?\s*(?:customers?|buyers?|reviews?|ratings?)\b/i,
  /\b(?:join|over|more\s+than)?\s*\d[\d,\s.]*\+?\s+(?:\w+\s+){0,3}(?:customers?|buyers?|reviews?|ratings?)\b/i,
  /\b\d(?:\.\d+)?\s*(?:\/\s*5|stars?)\b/i,
  /\b(?:five[\s-]?star|top[\s-]?rated|rated|rating)\b/i,
  /\b(?:hundreds?|thousands?|millions?)\s+(?:of\s+)?(?:customers?|buyers?|people|reviews?)\b/i,
  /\b(?:trusted|loved|chosen|recommended)\s+by\b/i,
  /\b(?:subscribe|subscription|recurring|auto[\s-]?(?:renew|ship|delivery)|cancel\s+anytime|refill\s+every|ships?\s+every)\b/i,
  /\b(?:shopify|variant|sku)\b/i,
  /\b(?:shopify\s+)?variant(?:\s+id)?\s*[:#]?\s*\d{5,}\b/i,
] as const;

export function papabearGeneratedTextIsClaimSafe(value: unknown): value is string {
  if (typeof value !== "string" || value.trim().length === 0) return false;
  return !PAPABEAR_UNSAFE_GENERATED_CLAIM_PATTERNS.some((pattern) =>
    pattern.test(value),
  );
}

export function claimSafePapabearGeneratedText(
  candidate: unknown,
  currentValue: string,
): string {
  return papabearGeneratedTextIsClaimSafe(candidate)
    ? candidate.trim()
    : currentValue;
}

export function mergeLpPapabearLabelSection<
  K extends keyof LpPapabearLabelContent,
>(
  current: unknown,
  sectionKey: K,
  section: LpPapabearLabelContent[K],
): LpPapabearLabelContent {
  const normalizedCurrent = normalizeLpPapabearLabelContent(current);
  return normalizeLpPapabearLabelContent({
    ...normalizedCurrent,
    [sectionKey]: section,
  });
}

// ── Per-section regen key helpers ─────────────────────────────────────────────
// Dynamic block keys follow the convention "papabear-<sectionKey>", mirroring
// the cold-drip "cold-drip-<sectionKey>" precedent.

export function lpPapabearLabelRegenKey(sectionKey: string): string {
  return `papabear-${sectionKey}`;
}

export const LP_PAPABEAR_LABEL_REGEN_KEY_RE =
  /^papabear-(hero|reasons|buyBox|reviews|faq|stickyBar)$/;
